import { useEffect, useState } from 'react';
import api from '../../services/api';
import Button from '../../components/Button';
import Input from '../../components/Input';
import Modal from '../../components/Modal';

interface SubClaim {
  id: string;
  code: string;
  name: string;
}
interface Claim {
  id: string;
  code: string;
  name: string;
  subClaims: SubClaim[];
}
interface ClaimGroup {
  id: string;
  code: string;
  name: string;
  claims: Claim[];
}

export default function ClaimGroups() {
  const [groups, setGroups] = useState<ClaimGroup[]>([]);
  const [groupForm, setGroupForm] = useState<{ show: boolean; code: string; name: string }>({ show: false, code: '', name: '' });
  const [claimForm, setClaimForm] = useState<{ show: boolean; groupId: string; code: string; name: string }>({ show: false, groupId: '', code: '', name: '' });
  const [subClaimForm, setSubClaimForm] = useState<{ show: boolean; claimId: string; code: string; name: string }>({ show: false, claimId: '', code: '', name: '' });
  const [error, setError] = useState('');

  const load = async () => {
    const res = await api.get('/ecs/masters/claim-groups');
    setGroups(res.data.claimGroups || []);
  };

  useEffect(() => {
    load();
  }, []);

  const saveGroup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      await api.post('/ecs/masters/claim-groups', { code: groupForm.code, name: groupForm.name });
      setGroupForm({ show: false, code: '', name: '' });
      load();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save');
    }
  };

  const deleteGroup = async (id: string) => {
    if (!window.confirm('Delete this claim group and everything under it?')) return;
    await api.delete(`/ecs/masters/claim-groups/${id}`);
    load();
  };

  const saveClaim = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      await api.post(`/ecs/masters/claim-groups/${claimForm.groupId}/claims`, { code: claimForm.code, name: claimForm.name });
      setClaimForm({ show: false, groupId: '', code: '', name: '' });
      load();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save');
    }
  };

  const deleteClaim = async (id: string) => {
    if (!window.confirm('Delete this claim and its sub-claims?')) return;
    await api.delete(`/ecs/masters/claims/${id}`);
    load();
  };

  const saveSubClaim = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      await api.post(`/ecs/masters/claims/${subClaimForm.claimId}/sub-claims`, { code: subClaimForm.code, name: subClaimForm.name });
      setSubClaimForm({ show: false, claimId: '', code: '', name: '' });
      load();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save');
    }
  };

  const deleteSubClaim = async (id: string) => {
    if (!window.confirm('Delete this sub-claim?')) return;
    await api.delete(`/ecs/masters/sub-claims/${id}`);
    load();
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-semibold text-gray-800">Claim Group &rarr; Claim &rarr; Sub-Claim</h1>
        <Button onClick={() => setGroupForm({ show: true, code: '', name: '' })}>+ Claim Group</Button>
      </div>

      {groups.length === 0 && (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500 text-sm">
          No claim groups yet — click "+ Claim Group" to build the Claim Group → Claim → Sub-Claim hierarchy.
        </div>
      )}

      <div className="flex flex-col gap-4">
        {groups.map((g) => (
          <div key={g.id} className="border rounded-lg p-4 bg-white">
            <div className="flex items-center justify-between">
              <div className="font-semibold text-gray-800">
                {g.code} — {g.name}
              </div>
              <div className="flex gap-2">
                <Button variant="ghost" onClick={() => setClaimForm({ show: true, groupId: g.id, code: '', name: '' })}>
                  + Claim
                </Button>
                <Button variant="ghost" className="text-red-600" onClick={() => deleteGroup(g.id)}>
                  Delete Group
                </Button>
              </div>
            </div>
            <div className="pl-6 mt-3 flex flex-col gap-2">
              {g.claims.map((c) => (
                <div key={c.id} className="border-l-2 pl-3">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-700">
                      {c.code} — {c.name}
                    </span>
                    <div className="flex gap-2">
                      <Button variant="ghost" onClick={() => setSubClaimForm({ show: true, claimId: c.id, code: '', name: '' })}>
                        + Sub-Claim
                      </Button>
                      <Button variant="ghost" className="text-red-600" onClick={() => deleteClaim(c.id)}>
                        Delete
                      </Button>
                    </div>
                  </div>
                  <ul className="pl-6 mt-1 text-sm text-gray-600">
                    {c.subClaims.map((sc) => (
                      <li key={sc.id} className="flex items-center justify-between py-0.5">
                        <span>
                          {sc.code} — {sc.name}
                        </span>
                        <button className="text-red-600 hover:underline text-xs" onClick={() => deleteSubClaim(sc.id)}>
                          Delete
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <Modal isOpen={groupForm.show} onClose={() => setGroupForm({ ...groupForm, show: false })} title="New Claim Group">
        <form onSubmit={saveGroup} className="flex flex-col gap-4">
          {error && <div className="text-sm text-red-600">{error}</div>}
          <Input label="Code" maxLength={6} required value={groupForm.code} onChange={(e) => setGroupForm({ ...groupForm, code: e.target.value })} />
          <Input label="Name" maxLength={50} required value={groupForm.name} onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })} />
          <Button type="submit">Save</Button>
        </form>
      </Modal>

      <Modal isOpen={claimForm.show} onClose={() => setClaimForm({ ...claimForm, show: false })} title="New Claim">
        <form onSubmit={saveClaim} className="flex flex-col gap-4">
          {error && <div className="text-sm text-red-600">{error}</div>}
          <Input label="Code" maxLength={6} required value={claimForm.code} onChange={(e) => setClaimForm({ ...claimForm, code: e.target.value })} />
          <Input label="Name" maxLength={50} required value={claimForm.name} onChange={(e) => setClaimForm({ ...claimForm, name: e.target.value })} />
          <Button type="submit">Save</Button>
        </form>
      </Modal>

      <Modal isOpen={subClaimForm.show} onClose={() => setSubClaimForm({ ...subClaimForm, show: false })} title="New Sub-Claim">
        <form onSubmit={saveSubClaim} className="flex flex-col gap-4">
          {error && <div className="text-sm text-red-600">{error}</div>}
          <Input label="Code" maxLength={6} required value={subClaimForm.code} onChange={(e) => setSubClaimForm({ ...subClaimForm, code: e.target.value })} />
          <Input label="Name" maxLength={50} required value={subClaimForm.name} onChange={(e) => setSubClaimForm({ ...subClaimForm, name: e.target.value })} />
          <Button type="submit">Save</Button>
        </form>
      </Modal>
    </div>
  );
}
